import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Icon, Button } from '@iq/react-components';

import {
  getNotifications,
  removeNotification,
  markNotificationsRead,
  cancelNotificationTimer,
  startNotificationTimer,
  setPanelOpened,
  TYPES,
} from '../../bundles/notifications';
import { isDoNotDisturb } from '../../bundles/notifications/doNotDisturb';
import { getActiveSiteId } from '../../bundles/sites';
import NotificationsPanel from './NotificationsPanel';
import GlobalMessagePortal from './GlobalMessagePortal';
import Notification from './Notification';
import Pill from '../Pill';

const Notifications = ({ portalOnly = false }) => {
  const dispatch = useDispatch();
  const notifications = useSelector(getNotifications);
  const activeSiteId = useSelector(getActiveSiteId);
  const [showPanel, setShowPanel] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setTimeout(() => {
          if (showPanel) {
            dispatch(markNotificationsRead());
            dispatch(setPanelOpened());
          }
          setShowPanel(false);
        }, 0);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPanel]);

  const toggleNotificationsPanel = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (showPanel) {
      dispatch(markNotificationsRead());
      dispatch(setPanelOpened());
    }
    setShowPanel(!showPanel);
  };

  const groupedNotifications = useMemo(() => {
    const grouped = Object.values(
      notifications.reduce((acc, notification) => {
        const { groupId, id } = notification;
        if (groupId) {
          if (acc[groupId]) {
            acc[groupId].items = [...acc[groupId].items, notification];
          } else {
            acc[groupId] = { ...notification, items: [notification] };
          }
        } else {
          acc[id] = { ...notification, items: [] };
        }
        return acc;
      }, {})
    );
    return grouped.map((group) => {
      if (group.items.length > 1) {
        return {
          ...group,
          visible: group.items.some((n) => n.visible),
          read: group.items.every((n) => n.read),
        };
      }
      return group;
    });
  }, [notifications]);

  const visible = useMemo(
    () => groupedNotifications.filter((n) => n.visible),
    [groupedNotifications]
  );
  const displayed = visible[0];
  const expanded = expandedIds.includes(displayed?.id);
  const panelsToRender = displayed && displayed.items.length > 1 ? 3 : 1;

  useEffect(() => {
    dispatch(cancelNotificationTimer());
    if (displayed) {
      if (displayed.items.length > 0) {
        for (const item of displayed.items) {
          dispatch(startNotificationTimer(item.id, item.hideTimeout));
        }
      } else {
        dispatch(startNotificationTimer(displayed.id, displayed.hideTimeout));
      }
    }
  }, [
    displayed?.id,
    displayed?.hideTimeout,
    displayed?.items?.map((index) => `${index.id}:${index.hideTimeout}`).join(','),
  ]);

  const readable = useMemo(
    () => groupedNotifications.filter((n) => n.type !== TYPES.transient),
    [groupedNotifications]
  );

  const unread = useMemo(() => readable.filter((n) => !n.read), [groupedNotifications]);

  const handleRemoveNotificationById = (event, id) => {
    event.stopPropagation();
    event.preventDefault();
    dispatch(removeNotification(id));
  };

  const handleCancelTimer = (event) => {
    event.stopPropagation();
    event.preventDefault();
    dispatch(cancelNotificationTimer());
  };

  const handleRestartTimer = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (displayed) {
      if (displayed.items.length > 0) {
        for (const item of displayed.items) {
          dispatch(startNotificationTimer(item.id, item.hideTimeout));
        }
      } else {
        dispatch(startNotificationTimer(displayed.id, displayed.hideTimeout));
      }
    }
  };

  const handleExpandNotificationGroup = (event, id) => {
    event.stopPropagation();
    event.preventDefault();
    if (groupedNotifications.some((n) => n.id === id && n.items.length > 1)) {
      if (expandedIds.includes(id)) {
        setExpandedIds((prev) => prev.filter((eid) => eid !== id));
      } else {
        setExpandedIds((prev) => [...prev, id]);
      }
    }
  };

  return (
    <>
      {!portalOnly && (
        <div
          className={`notifications--panel-toggler ${unread.length === 0 ? 'muted' : ''}`}
          ref={ref}
        >
          <Button
            design="text"
            tooltip="Notifications"
            onClick={toggleNotificationsPanel}
          >
            <Icon
              icon="he-alert"
              fill="gray"
            />
          </Button>
          {unread.length > 0 && <Pill text={unread.length} />}
          {showPanel && (
            <NotificationsPanel
              notifications={readable}
              onClear={handleRemoveNotificationById}
              siteId={activeSiteId}
            />
          )}
        </div>
      )}

      <GlobalMessagePortal>
        {!isDoNotDisturb(activeSiteId) && (
          <div
            data-testid="notifications-overlay"
            className={`notifications--overlay ${displayed ? 'visible' : 'hidden'}`}
            onMouseDown={(event) => {
              event.stopPropagation();
              event.preventDefault();
            }}
            onMouseOver={handleCancelTimer}
            onMouseOut={handleRestartTimer}
            onFocus={handleCancelTimer}
            onBlur={handleRestartTimer}
          >
            {displayed?.visible &&
              !expanded &&
              Array.from({ length: panelsToRender }).map((_, index) => (
                <Notification
                  key={`${displayed.id}-${panelsToRender - 1 - index}`}
                  notification={displayed}
                  depth={panelsToRender - 1 - index}
                  colorPanel={panelsToRender === 1 || index === 2}
                  onClear={handleRemoveNotificationById}
                  onExpand={handleExpandNotificationGroup}
                  onClick={(event) => handleExpandNotificationGroup(event, displayed.id)}
                  stacked
                />
              ))}
            {displayed && expanded && (
              <>
                <div className="collapser">
                  <Button
                    className="clear"
                    activity="secondary"
                    slim
                    onClick={(event) => handleExpandNotificationGroup(event, displayed.id)}
                  >
                    Collapse
                  </Button>
                </div>
                {displayed.items.map(
                  (item, index) =>
                    item?.visible && (
                      <Notification
                        key={`${displayed.id}-${index}`}
                        notification={item}
                        onClear={handleRemoveNotificationById}
                        colorPanel
                      />
                    )
                )}
              </>
            )}
          </div>
        )}
      </GlobalMessagePortal>
    </>
  );
};

export default Notifications;
